import fs from 'fs/promises'
import path from 'path'
import os from 'os'
import { InterfaceDefinition } from './types.js'

interface VersionResolution {
  interfaceId: string
  updatedAt: string
  cachedAt: string
}

interface CachedDefinition {
  definition: InterfaceDefinition
  cachedAt: string
}

export class EnhancedInterfaceCache {
  private cacheDir: string
  private resolutionsDir: string
  private definitionsDir: string
  private noCache: boolean

  constructor(noCache: boolean = false) {
    this.noCache = noCache
    this.cacheDir = path.join(os.homedir(), '.adk', 'cache', 'interfaces')
    this.resolutionsDir = path.join(this.cacheDir, 'resolutions')
    this.definitionsDir = path.join(this.cacheDir, 'definitions')
  }

  private async ensureCacheDirs(): Promise<void> {
    await fs.mkdir(this.resolutionsDir, { recursive: true })
    await fs.mkdir(this.definitionsDir, { recursive: true })
  }

  async getResolution(name: string, version: string, workspace?: string): Promise<VersionResolution | null> {
    if (this.noCache) return null
    try {
      const key = this.getResolutionKey(name, version, workspace)
      const cachePath = path.join(this.resolutionsDir, `${key}.json`)
      const data = await fs.readFile(cachePath, 'utf-8')
      const resolution: VersionResolution = JSON.parse(data)
      const cachedAt = new Date(resolution.cachedAt)
      const ageMinutes = (Date.now() - cachedAt.getTime()) / (1000 * 60)
      if (ageMinutes > 5) return null
      return resolution
    } catch {
      return null
    }
  }

  async setResolution(
    name: string,
    version: string,
    workspace: string | undefined,
    interfaceId: string,
    updatedAt: string
  ): Promise<void> {
    await this.ensureCacheDirs()
    const key = this.getResolutionKey(name, version, workspace)
    const cachePath = path.join(this.resolutionsDir, `${key}.json`)
    const resolution: VersionResolution = {
      interfaceId,
      updatedAt,
      cachedAt: new Date().toISOString(),
    }
    await fs.writeFile(cachePath, JSON.stringify(resolution, null, 2))
  }

  async getDefinition(interfaceId: string, updatedAt: string): Promise<InterfaceDefinition | null> {
    if (this.noCache) return null
    try {
      const key = this.getDefinitionKey(interfaceId, updatedAt)
      const cachePath = path.join(this.definitionsDir, `${key}.json`)
      const data = await fs.readFile(cachePath, 'utf-8')
      const cached: CachedDefinition = JSON.parse(data)
      return cached.definition
    } catch {
      return null
    }
  }

  async setDefinition(interfaceId: string, updatedAt: string, definition: InterfaceDefinition): Promise<void> {
    await this.ensureCacheDirs()
    const key = this.getDefinitionKey(interfaceId, updatedAt)
    const cachePath = path.join(this.definitionsDir, `${key}.json`)
    const cached: CachedDefinition = {
      definition,
      cachedAt: new Date().toISOString(),
    }
    await fs.writeFile(cachePath, JSON.stringify(cached, null, 2))
  }

  async clear(): Promise<void> {
    try {
      const resolutionFiles = await fs.readdir(this.resolutionsDir)
      await Promise.all(resolutionFiles.map((f) => fs.unlink(path.join(this.resolutionsDir, f))))
      const definitionFiles = await fs.readdir(this.definitionsDir)
      await Promise.all(definitionFiles.map((f) => fs.unlink(path.join(this.definitionsDir, f))))
    } catch {
      // ignore
    }
  }

  async getStats(): Promise<{
    resolutions: { count: number; sizeBytes: number }
    definitions: { count: number; sizeBytes: number }
  }> {
    const getDirectoryStats = async (dir: string) => {
      try {
        const files = await fs.readdir(dir)
        let totalSize = 0
        for (const file of files) {
          const stats = await fs.stat(path.join(dir, file))
          totalSize += stats.size
        }
        return { count: files.length, sizeBytes: totalSize }
      } catch {
        return { count: 0, sizeBytes: 0 }
      }
    }
    const [resolutions, definitions] = await Promise.all([
      getDirectoryStats(this.resolutionsDir),
      getDirectoryStats(this.definitionsDir),
    ])
    return { resolutions, definitions }
  }

  private getResolutionKey(name: string, version: string, workspace?: string): string {
    const prefix = workspace ? `${workspace}_` : ''
    const key = `${prefix}${name}_${version}`
    return key.replace(/[^a-zA-Z0-9_-]/g, '_')
  }

  private getDefinitionKey(interfaceId: string, updatedAt: string): string {
    const key = `${interfaceId}_${updatedAt}`
    return key.replace(/[^a-zA-Z0-9_-]/g, '_')
  }
}
